import { useEffect, useState } from 'react';

/**
 * Herbruikbare paginatie-balk + hook voor alle lijst-pagina's.
 *
 * Gebruik:
 *   const { pageSize, setPageSize, curPage, setPage, paged } = usePagination(filteredItems, 'klanten');
 *   {paged.map(item => ...)}
 *   <Pagination total={filteredItems.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} />
 */

export function usePagination(items, storageKey = 'default', defaultSize = 25) {
  const storage = `pagination_${storageKey}`;
  const [pageSize, setPageSize] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(storage) || String(defaultSize), 10);
      return v > 0 && v <= 1000 ? v : defaultSize;
    } catch { return defaultSize; }
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    try { localStorage.setItem(storage, String(pageSize)); } catch { /* quota */ }
  }, [pageSize, storage]);

  // Reset naar pagina 1 bij wijziging van items (bv. na filter/zoek) of pageSize
  useEffect(() => { setPage(1); }, [items.length, pageSize]);

  const totalPages = Math.max(1, Math.ceil((items.length || 0) / pageSize));
  const curPage = Math.min(Math.max(1, page), totalPages);
  const paged = items.slice((curPage - 1) * pageSize, curPage * pageSize);

  return { pageSize, setPageSize, curPage, setPage, paged, totalPages };
}

export default function Pagination({ total, pageSize, setPageSize, curPage, setPage, presets = [25, 50, 100, 250] }) {
  if (!total || total === 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex-wrap"
      data-testid="pagination-bar"
    >
      <div className="flex flex-wrap max-w-full items-center gap-2 text-[11px] text-gray-600">
        <label className="font-bold uppercase tracking-wider text-gray-500">Per pagina</label>
        <input
          type="number"
          min="1"
          max="1000"
          step="1"
          className="w-20 px-2 py-1 border border-gray-200 rounded text-xs font-mono font-bold text-center focus:outline-none focus:border-[#002FA7]"
          value={pageSize}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v > 0 && v <= 1000) setPageSize(v);
          }}
          data-testid="pagesize-input"
          title="Aantal per pagina (1–1000)"
        />
        <span className="text-gray-400">·</span>
        <span className="text-gray-500">
          {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, total)} van{' '}
          <strong className="text-gray-700">{total}</strong>
        </span>
        {presets.map((sz) => (
          <button
            key={sz}
            type="button"
            onClick={() => setPageSize(sz)}
            className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors ${
              pageSize === sz ? 'bg-[#002FA7] text-white' : 'text-gray-500 hover:bg-gray-200'
            }`}
            data-testid={`pagesize-preset-${sz}`}
          >
            {sz}
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap max-w-full items-center gap-1" data-testid="pagination-nav">
          <button
            type="button"
            className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={curPage === 1}
            onClick={() => setPage(1)}
            data-testid="pagination-first"
            title="Eerste pagina"
          >‹‹</button>
          <button
            type="button"
            className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={curPage === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="pagination-prev"
            title="Vorige pagina"
          >‹</button>
          {(() => {
            const pages = [];
            let start = Math.max(1, curPage - 3);
            let end = Math.min(totalPages, start + 6);
            if (end - start < 6) start = Math.max(1, end - 6);
            for (let p = start; p <= end; p++) pages.push(p);
            return pages.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`min-w-[28px] px-2 py-1 text-xs font-bold rounded transition-colors ${
                  p === curPage ? 'bg-[#D97706] text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`pagination-page-${p}`}
              >{p}</button>
            ));
          })()}
          <button
            type="button"
            className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={curPage === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="pagination-next"
            title="Volgende pagina"
          >›</button>
          <button
            type="button"
            className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={curPage === totalPages}
            onClick={() => setPage(totalPages)}
            data-testid="pagination-last"
            title="Laatste pagina"
          >››</button>
        </div>
      )}
    </div>
  );
}
